/**
 * BaseService - Abstract base class providing common functionality for all API services
 *
 * Provides:
 * - Centralized API URL configuration
 * - Auth token management
 * - Request headers
 * - Response handling with error extraction
 * - Timeout handling with Promise.race pattern
 */

export type ApiUrlType = 'cro' | 'atlas' | 'report' | 'scout'

interface ApiUrlConfig {
  envVar: string
  localUrl: string
  remoteUrl: string
}

const API_URL_CONFIGS: Record<ApiUrlType, ApiUrlConfig> = {
  cro: {
    envVar: 'VITE_CRO_API_URL',
    localUrl: 'http://localhost:3007/api/v1',
    remoteUrl: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  },
  atlas: {
    envVar: 'VITE_API_BASE_URL',
    localUrl: 'http://localhost:3001/api/v1',
    remoteUrl: 'https://ath-atlas-api-uat-west.azurewebsites.net/api/v1'
  },
  report: {
    envVar: 'VITE_REPORT_API_URL',
    localUrl: 'http://localhost:3006/api/v1',
    remoteUrl: 'https://ath-avi-reporting-uat-west.azurewebsites.net/api/v1'
  },
  scout: {
    envVar: 'VITE_SCOUT_API_URL',
    localUrl: 'http://localhost:3009/api/v1',
    remoteUrl: 'https://scout.uat.avi.athletiverse.io/api/v1'
  }
}

/**
 * Get API URL for a given API type
 */
export function getApiUrl(type: ApiUrlType): string {
  const config = API_URL_CONFIGS[type]
  const envUrl = import.meta.env[config.envVar]
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  return mode === 'remote' ? config.remoteUrl : config.localUrl
}

/**
 * Default timeout in milliseconds
 */
export const DEFAULT_TIMEOUT_MS = 30000

/**
 * Abstract base class for API services
 */
export abstract class BaseService {
  protected baseURL: string

  constructor(apiType: ApiUrlType) {
    this.baseURL = getApiUrl(apiType)
  }

  /**
   * Get auth token from localStorage
   */
  protected getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  /**
   * Get standard request headers with optional auth token
   */
  protected getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  /**
   * Handle API response - parse JSON and throw on errors
   */
  protected async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const error = new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      ) as Error & { status?: number; code?: string }
      // Callers need to tell "you may not" from "it broke" — a 403 on a gated
      // feature is an expected state to render, not an error to toast. Attached
      // rather than thrown differently so existing callers reading .message are
      // untouched.
      error.status = response.status
      error.code = errorData.error?.code
      throw error
    }
    return response.json()
  }

  /**
   * Create a timeout promise that rejects after specified milliseconds
   */
  protected createTimeout(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<never> {
    return new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })
  }

  /**
   * Execute fetch with timeout
   */
  protected async fetchWithTimeout<T>(
    url: string | URL,
    config: RequestInit,
    timeoutMs: number = DEFAULT_TIMEOUT_MS
  ): Promise<T> {
    const response = await Promise.race([
      fetch(url.toString(), config),
      this.createTimeout(timeoutMs)
    ])
    return this.handleResponse<T>(response)
  }

  /**
   * Build URL with query parameters
   * Handles arrays by appending multiple values for the same key
   */
  protected buildUrl(
    endpoint: string,
    params?: Record<string, string | number | boolean | string[] | undefined | null>
  ): URL {
    const url = new URL(`${this.baseURL}${endpoint}`)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return

        if (Array.isArray(value)) {
          value.forEach(v => url.searchParams.append(key, v))
        } else {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url
  }

  /**
   * GET request helper
   */
  protected async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | string[] | undefined | null>,
    timeoutMs?: number
  ): Promise<T> {
    const url = this.buildUrl(endpoint, params)
    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }
    return this.fetchWithTimeout<T>(url, config, timeoutMs)
  }

  /**
   * POST request helper
   */
  protected async post<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, string | number | boolean | string[] | undefined | null>,
    timeoutMs?: number
  ): Promise<T> {
    const url = this.buildUrl(endpoint, params)
    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(body)
    }
    return this.fetchWithTimeout<T>(url, config, timeoutMs)
  }

  /**
   * PUT request helper
   */
  protected async put<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, string | number | boolean | string[] | undefined | null>,
    timeoutMs?: number
  ): Promise<T> {
    const url = this.buildUrl(endpoint, params)
    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(body)
    }
    return this.fetchWithTimeout<T>(url, config, timeoutMs)
  }

  /**
   * PATCH request helper
   */
  protected async patch<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, string | number | boolean | string[] | undefined | null>,
    timeoutMs?: number
  ): Promise<T> {
    const url = this.buildUrl(endpoint, params)
    const config: RequestInit = {
      method: 'PATCH',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(body)
    }
    return this.fetchWithTimeout<T>(url, config, timeoutMs)
  }

  /**
   * DELETE request helper
   */
  protected async delete<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | string[] | undefined | null>,
    timeoutMs?: number
  ): Promise<T> {
    const url = this.buildUrl(endpoint, params)
    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }
    return this.fetchWithTimeout<T>(url, config, timeoutMs)
  }
}

export default BaseService
